<template>
  <div class="view-page-container">
    <div>
      <div class="unified-detail-card">
        <!-- ── HEADER ── -->
        <div class="udc-header pb-4">
          <div class="udc-profile-top">
            <div class="udc-profile-left">
              <div class="udc-avatar-wrap" style="border-radius: 16px">
                <div
                  class="udc-placeholder"
                  style="
                    background: var(--sb-act-bg) !important;
                    color: var(--brand);
                  "
                >
                  <v-icon size="32" color="var(--brand)"
                    >mdi-ticket-confirmation-outline</v-icon
                  >
                </div>
              </div>
              <div class="udc-info">
                <h2 class="udc-name">{{ complaint.title || "Loading..." }}</h2>
                <div class="udc-meta-row mt-2" v-if="complaint.id">
                  <div class="udc-meta-item">
                    <span
                      class="status-dot"
                      :class="getStatusClass(complaint.status)"
                    ></span>
                    <span class="udc-meta-text">{{
                      getStatusText(complaint.status)
                    }}</span>
                  </div>
                  <span class="udc-meta-sep">|</span>
                  <div class="udc-meta-item">
                    <v-icon size="14" color="#64748B">mdi-flag</v-icon>
                    <span class="udc-meta-text"
                      >{{ getPriorityText(complaint.priority) }} Priority</span
                    >
                  </div>
                  <span class="udc-meta-sep" v-if="complaint.added_by">|</span>
                  <div class="udc-meta-item" v-if="complaint.added_by">
                    <v-icon size="14" color="#64748B"
                      >mdi-account-outline</v-icon
                    >
                    <span class="udc-meta-text"
                      >By {{ complaint.added_by }}</span
                    >
                  </div>
                </div>
              </div>
            </div>
            <div class="udc-profile-right">
              <button
                class="udc-back-btn"
                @click="$router.go(-1)"
                :style="{ color: buttonColor, borderColor: buttonColor }"
              >
                <v-icon size="16" class="arrow" :style="{ color: buttonColor }"
                  >mdi-arrow-left</v-icon
                >
                BACK
              </button>
            </div>
          </div>
        </div>

        <!-- ── CONTENT ── -->
        <div class="udc-content-wrap" v-if="complaint.id">
          <!-- Ticket Profile -->
          <div class="premium-field-label mb-4">Ticket profile</div>
          <div class="info-grid mb-8">
            <div class="info-cell">
              <div class="cell-label">Title</div>
              <div class="cell-value">{{ complaint.title }}</div>
            </div>
            <div class="info-cell">
              <div class="cell-label">Added by</div>
              <div class="cell-value">{{ complaint.added_by || "N/A" }}</div>
            </div>
            <div class="info-cell">
              <div class="cell-label">House / Unit</div>
              <div class="cell-value">
                <span v-if="complaint.flat_name"
                  >{{ complaint.flat_name }}-</span
                >
                <span>{{ complaint.house_number || "N/A" }}</span>
              </div>
            </div>
            <div class="info-cell">
              <div class="cell-label">Status</div>
              <div class="cell-value">
                <div class="status-pill-wrap">
                  <div
                    class="status-chip"
                    :style="getStatusStyles(complaint.status)"
                    @click.stop="toggleStatusMenu($event)"
                  >
                    <span
                      class="status-chip-dot"
                      :style="{ background: getStatusColor(complaint.status) }"
                    ></span>
                    <span>{{ getStatusText(complaint.status) }}</span>
                    <v-icon
                      size="12"
                      :color="getStatusColor(complaint.status)"
                      class="ml-1"
                      >mdi-chevron-down</v-icon
                    >
                  </div>
                </div>
              </div>
            </div>
            <div class="info-cell">
              <div class="cell-label">Priority</div>
              <div class="cell-value">
                <span
                  class="type-pill"
                  :class="getPriorityClass(complaint.priority)"
                >
                  {{ getPriorityText(complaint.priority) }}
                </span>
              </div>
            </div>
            <div class="info-cell">
              <div class="cell-label">Created on</div>
              <div class="cell-value">
                {{ complaint.created_on | formatDateTime }}
              </div>
            </div>
            <div class="info-cell full">
              <div class="cell-label">Description</div>
              <div class="cell-value desc">
                {{ complaint.description || "No description provided." }}
              </div>
            </div>
          </div>

          <!-- Reference Documents -->
          <div class="notice-attachments-section mb-8">
            <div class="premium-field-label mb-4">Reference documents</div>
            <div class="doc-grid" v-if="added_documents && added_documents.length > 0">
              <div
                v-for="(item, index) in added_documents"
                :key="item.id || item.document_url"
                class="doc-card"
                @click="imgClick(index)"
              >
                <v-img
                  v-if="item.file_type === 'image'"
                  :src="base_url + '/uploads/' + item.document_url"
                  class="doc-card-img"
                  contain
                />
                <div v-else class="doc-card-placeholder">
                  <v-icon color="#EF4444" size="52">mdi-file-pdf-box</v-icon>
                  <span class="doc-card-placeholder-label">PDF</span>
                </div>
                <div class="doc-card-overlay">
                  <div class="doc-overlay-btn">
                    <v-icon size="16" color="white">mdi-eye-outline</v-icon>
                    <span>View</span>
                  </div>
                </div>
              </div>
            </div>
            <div v-else class="empty-state">
              <div class="empty-icon">
                <v-icon size="20" color="#9CA3AF">mdi-file-hidden</v-icon>
              </div>
              <span class="empty-text">No documents attached</span>
            </div>
          </div>
        </div>

        <!-- Loading state -->
        <div v-else class="udc-content-wrap loading-state">
          <v-progress-circular
            indeterminate
            color="var(--brand, #0D9972)"
            size="32"
          />
        </div>
      </div>
    </div>

    <!-- Media Viewer -->
    <MediaViewer
      v-model="imageDialog"
      :items="mediaItems"
      :index="mediaIndex"
    />

    <!-- ── Status Floating Dropdown ── -->
    <div
      v-if="statusMenu.open"
      class="status-dropdown-portal"
      :style="{ top: statusMenu.y + 'px', left: statusMenu.x + 'px' }"
      @click.stop
    >
      <div
        v-for="s in statusList"
        :key="s.value"
        class="status-option"
        :class="{ active: complaint.status === s.value }"
        @click="selectStatus(s.value)"
      >
        <span class="status-chip-dot" :style="{ background: s.color }"></span>
        <span>{{ s.name }}</span>
        <v-icon
          v-if="complaint.status === s.value"
          size="12"
          color="var(--brand)"
          class="ml-auto"
          >mdi-check</v-icon
        >
      </div>
    </div>
  </div>
</template>
<script>
import { mapMutations, mapState } from "vuex";
import moment from "moment";
import MediaViewer from "../../common/MediaViewer.vue";

export default {
  components: { MediaViewer },
  data() {
    return {
      user: {},
      loading: false,
      serverSideErrors: {},

      complaint: {
        title: "",
        description: "",
        picture_url: "",
      },
      added_documents: [],
      base_url: process.env.VUE_APP_API_URL,
      imageDialog: false,
      image_url: "",
      activeItem: null,
      mediaIndex: 0,
      statusList: [
        { name: "Pending", value: 0, color: "#F59E0B" },
        { name: "In Progress", value: 1, color: "#10B981" },
        { name: "Completed", value: 2, color: "#3B82F6" },
        { name: "Cancelled", value: 3, color: "#EF4444" },
      ],
      statusMenu: { open: false, x: 0, y: 0 },
    };
  },
  computed: {
    ...mapState("theme", ["buttonColor"]),
    mediaItems() {
      return this.added_documents.map((doc) => ({
        url: this.base_url + "/uploads/" + doc.document_url,
        type: doc.file_type === "image" ? "image" : "document",
        name: doc.name || doc.document_url,
      }));
    },
  },
  methods: {
    ...mapMutations("alert", ["setAlert"]),
    getComplaintsDetail() {
      let _self = this;
      _self.$axios
        .get("societyadmin/complaints/" + _self.$route.params.id)
        .then((response) => {
          let data = response.data;
          if (data.status) {
            _self.complaint = data.complaint;
            this.complaint.documents.map((x) => {
              x.name = x.document_url.replace("complaint_documents/", "");
            });

            _self.added_documents = data.complaint.documents;
          } else {
            if (data.errors) {
              _self.serverSideErrors = {};

              for (let i = 0; i < data.errors.length; i++) {
                _self.serverSideErrors[data.errors[i].field] =
                  data.errors[i].message;
              }
            } else {
              this.setAlert({
                showAlert: true,
                variant: "red accent-3",
                timeout: "2000",
                message: data.message,
              });
            }
          }
        });
    },
    getFormattedDate(date) {
      return date ? moment(date).format("DD MMM, YYYY · hh:mm A") : "N/A";
    },
    getStatusText(status) {
      const map = {
        0: "Pending",
        1: "In Progress",
        2: "Completed",
        3: "Cancelled",
      };
      return map[status] || "Unknown";
    },
    getStatusClass(status) {
      const map = {
        0: "s-pending",
        1: "s-progress",
        2: "s-closed",
        3: "s-cancelled",
      };
      return map[status] || "";
    },
    getPriorityText(priority) {
      const map = { 1: "Low", 2: "Medium", 3: "High", 0: "Emergency" };
      return map[priority] || "General";
    },
    getPriorityClass(priority) {
      const map = { 1: "p-low", 2: "p-medium", 3: "p-high", 0: "p-emergency" };
      return map[priority] || "";
    },
    imgClick(index) {
      this.mediaIndex = index;
      this.imageDialog = true;
    },
    getStatusColor(s) {
      const colors = { 0: "#F59E0B", 1: "#10B981", 2: "#3B82F6", 3: "#EF4444" };
      return colors[s] ?? "#F59E0B";
    },
    getStatusStyles(s) {
      const color = this.getStatusColor(s);
      return {
        background: color + "15",
        color,
        border: `1px solid ${color}30`,
      };
    },
    toggleStatusMenu(event) {
      if (this.statusMenu.open) {
        this.statusMenu.open = false;
        return;
      }
      const rect = event.currentTarget.getBoundingClientRect();
      this.statusMenu = { open: true, x: rect.left, y: rect.bottom + 4 };
    },
    selectStatus(value) {
      this.statusMenu.open = false;
      if (this.complaint.status === value) return;
      this.complaint.status = value;
      this.$axios
        .put(
          `societyadmin/complaints/updateStatus/${this.complaint.id}/${value}`,
        )
        .then((response) => {
          if (response.data.status) {
            this.setAlert({
              showAlert: true,
              variant: "green accent-4",
              timeout: "2000",
              message: response.data.message,
            });
          }
        });
    },
    openInTab() {
      if (this.activeItem) {
        window.open(
          `${this.base_url}/uploads/${this.activeItem.document_url}`,
          "_blank",
        );
      }
    },
  },
  mounted() {
    this.user = JSON.parse(localStorage.getItem("user"));
    this.getComplaintsDetail();
    document.addEventListener("click", () => {
      this.statusMenu.open = false;
    });
  },
};
</script>
<style scoped>
.view-page-container {
  padding: 24px;
}

/* ── Unified Card ── */
.unified-detail-card {
  background: var(--surface);
  border-radius: 20px;
  overflow: hidden;
  border: 1px solid var(--border);
  display: flex;
  flex-direction: column;
}

/* ── Header ── */
.udc-header {
  padding: 16px 16px 0px 16px;
  border-bottom: 1px solid var(--border);
  background: var(--surface);
}
.udc-profile-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
  position: relative;
}
.udc-profile-left {
  display: flex;
  align-items: flex-start;
  gap: 24px;
  padding-right: 100px;
}
.udc-avatar-wrap {
  width: 64px;
  height: 64px;
  overflow: hidden;
  flex-shrink: 0;
  background: var(--surface2);
}
.udc-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}
.udc-info {
  flex: 1;
}
.udc-name {
  font-size: 20px;
  font-weight: 700;
  color: var(--tx);
  margin: 0 0 6px 0;
  letter-spacing: -0.01em;
  line-height: 1.2;
}
.udc-meta-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 14px;
}
.udc-meta-item {
  display: flex;
  align-items: center;
  gap: 6px;
}
.udc-meta-text {
  font-size: 13px;
  color: var(--tx2);
  font-weight: 500;
}
.udc-meta-sep {
  color: var(--border);
  font-size: 14px;
  opacity: 0.6;
}

/* ── Header Buttons ── */
.udc-profile-right {
  position: absolute;
  top: 0;
  right: 0;
  flex-shrink: 0;
}
.udc-back-btn {
  display: inline-flex !important;
  align-items: center !important;
  gap: 4px !important;
  padding: 0 14px 0 10px !important;
  font-weight: 700 !important;
  font-size: 12px !important;
  letter-spacing: 0.05em !important;
  border-radius: 8px !important;
  color: var(--btn-color) !important;
  border: 1px solid var(--btn-color) !important;
  background: var(--surface2) !important;
  height: 38px !important;
  cursor: pointer !important;
  transition: all 0.2s !important;
}
.udc-back-btn:hover {
  background: var(--surface2) !important;
  border-color: var(--btn-color) !important;
}

.arrow {
  color: var(--btn-color) !important;
}

/* ── Content Wrap ── */
.udc-content-wrap {
  background: var(--surface2);
  padding: 24px;
  min-height: 200px;
}
.loading-state {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 300px;
}

/* ── Section Label ── */
.premium-field-label {
  font-size: 11px;
  font-weight: 700;
  color: var(--tx3, #64748b);
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

/* ── Info Grid ── */
.info-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1px;
  background: var(--border);
  border: 1px solid var(--border);
  border-radius: 12px;
  overflow: hidden;
}
.info-cell {
  padding: 14px 16px;
  background: var(--surface);
}
.info-cell.full {
  grid-column: span 2;
}
.cell-label {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--tx3, #6b7280);
  margin-bottom: 5px;
}
.cell-value {
  font-size: 14px;
  font-weight: 600;
  color: var(--tx, #111827);
  line-height: 1.4;
}
.cell-value.desc {
  font-size: 13.5px;
  line-height: 1.65;
  white-space: pre-line;
  font-weight: 500;
  color: var(--tx2, #374151);
}

/* ── Status Chip in detail ── */
.status-pill-wrap {
  position: relative;
  display: inline-block;
}
.status-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px 4px 8px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.02em;
  cursor: pointer;
  user-select: none;
  transition: all 0.15s;
  white-space: nowrap;
}
.status-chip:hover {
  filter: brightness(0.95);
  transform: translateY(-1px);
}
.status-chip-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
}

/* ── Type Pills ── */
.type-pill {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 11.5px;
  font-weight: 700;
}
/* Priority */
.p-low {
  background: #f1f5f9;
  color: #64748b;
  border: 1px solid #cbd5e1;
}
.p-medium {
  background: #eff6ff;
  color: #2563eb;
  border: 1px solid #bfdbfe;
}
.p-high {
  background: #fef3c7;
  color: #d97706;
  border: 1px solid #fde68a;
}
.p-emergency {
  background: #fef2f2;
  color: #dc2626;
  border: 1px solid #fecaca;
}

/* Status */
.s-pending {
  background: #fffbeb;
  color: #d97706;
  border: 1px solid #fde68a;
}
.s-progress {
  background: #ecfdf5;
  color: #059669;
  border: 1px solid #a7f3d0;
}
.s-closed {
  background: #eff6ff;
  color: #2563eb;
  border: 1px solid #bfdbfe;
}
.s-cancelled {
  background: #fef2f2;
  color: #dc2626;
  border: 1px solid #fecaca;
}

/* Dark mode priority pills */
.theme--dark .p-low {
  background: rgba(255, 255, 255, 0.06);
  color: #A3A3A3;
  border-color: rgba(255, 255, 255, 0.12);
}
.theme--dark .p-medium {
  background: rgba(147, 197, 253, 0.1);
  color: #93C5FD;
  border-color: rgba(147, 197, 253, 0.2);
}
.theme--dark .p-high {
  background: rgba(253, 230, 138, 0.08);
  color: #FCD34D;
  border-color: rgba(253, 230, 138, 0.15);
}
.theme--dark .p-emergency {
  background: rgba(252, 165, 165, 0.08);
  color: #FCA5A5;
  border-color: rgba(252, 165, 165, 0.15);
}

/* Dark mode status pills */
.theme--dark .s-pending {
  background: rgba(253, 230, 138, 0.08);
  color: #FCD34D;
  border-color: rgba(253, 230, 138, 0.15);
}
.theme--dark .s-progress {
  background: rgba(52, 211, 153, 0.08);
  color: #34D399;
  border-color: rgba(52, 211, 153, 0.15);
}
.theme--dark .s-closed {
  background: rgba(147, 197, 253, 0.08);
  color: #93C5FD;
  border-color: rgba(147, 197, 253, 0.15);
}
.theme--dark .s-cancelled {
  background: rgba(252, 165, 165, 0.08);
  color: #FCA5A5;
  border-color: rgba(252, 165, 165, 0.15);
}

/* Dark mode attachment placeholder */
.theme--dark .attachment-placeholder {
  background: rgba(255, 255, 255, 0.04);
  /* width: 8px;
  height: 8px;
  border-radius: 50%; */
}
.s-pending.status-dot {
  background: #f59e0b;
  border: none;
}
.s-progress.status-dot {
  background: #10b981;
  border: none;
}
.s-closed.status-dot {
  background: #3b82f6;
  border: none;
}
.s-cancelled.status-dot {
  background: #ef4444;
  border: none;
}

/* ── Attachments ── */
.attachment-card-premium {
  position: relative;
  border-radius: 16px;
  overflow: hidden;
  aspect-ratio: 1;
  background: var(--surface);
  border: 1px solid var(--border);
  transition: all 0.25s ease;
  cursor: pointer;
}
.attachment-card-premium:hover {
  transform: translateY(-3px);
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.08);
}
.attachment-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}
.attachment-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fef2f2;
}
.attachment-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.38);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s;
}
.attachment-card-premium:hover .attachment-overlay {
  opacity: 1;
}
.overlay-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 16px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  font-size: 13px;
  font-weight: 700;
}

/* ── Empty State ── */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 36px 24px;
  gap: 10px;
  width: 25%;
  background: var(--surface);
  border-radius: 12px;
  border: 1px solid var(--border);
}
.empty-icon {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: var(--surface2);
  border: 1px solid var(--border);
  display: flex;
  align-items: center;
  justify-content: center;
}
.empty-text {
  font-size: 13px;
  font-weight: 500;
  color: var(--tx3);
  text-align: center;
}

/* ── Lightbox ── */
.lightbox-wrap {
  background: var(--surface);
  border-radius: 20px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  max-height: 90vh;
}

.lightbox-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  border-bottom: 1px solid var(--border);
  background: var(--surface2);
  flex-shrink: 0;
}

.lightbox-title {
  display: flex;
  align-items: center;
  font-size: 14px;
  font-weight: 700;
  color: var(--tx);
}

.lightbox-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.lb-btn {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid var(--border);
  background: var(--surface);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.15s;
}
.lb-btn:hover {
  background: var(--surface3);
  border-color: var(--brand);
}
.lb-close:hover {
  background: rgba(239, 68, 68, 0.08);
  border-color: #ef4444;
}
.lb-close:hover .v-icon {
  color: #ef4444 !important;
}

.lightbox-body {
  flex: 1;
  overflow: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #0a0a0a;
  min-height: 300px;
  max-height: calc(90vh - 60px);
}

.lightbox-img {
  max-width: 100%;
  max-height: calc(90vh - 60px);
  object-fit: contain;
  display: block;
}

.pdf-body {
  background: var(--surface2);
}

.lightbox-pdf {
  width: 100%;
  height: calc(90vh - 60px);
  min-height: 500px;
  border: none;
  display: block;
}

/* ── Responsive ── */
@media (max-width: 768px) {
  .udc-profile-top {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
  .udc-profile-left {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
  .udc-meta-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
  .udc-meta-sep {
    display: none;
  }
  .udc-profile-right {
    width: 100%;
    justify-content: flex-start;
  }
  .udc-content-wrap {
    padding: 16px;
  }
  .info-grid {
    grid-template-columns: 1fr;
  }
  .info-cell {
    border-bottom: 1px solid var(--border);
  }
}
</style>

<style>
.lightbox-dialog {
  border-radius: 20px !important;
  overflow: hidden !important;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.2) !important;
}

.status-dropdown-portal {
  position: fixed;
  z-index: 9999;
  background: var(--surface, #ffffff);
  border: 1px solid var(--border, #e2e8f0);
  border-radius: 12px;
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.14);
  min-width: 155px;
  overflow: hidden;
  animation: sdpFadeIn 0.15s ease;
}
@keyframes sdpFadeIn {
  from {
    opacity: 0;
    transform: translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
.status-dropdown-portal .status-option {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 9px 14px;
  font-size: 12.5px;
  font-weight: 600;
  color: var(--tx2, #374151);
  cursor: pointer;
  transition: background 0.12s;
}
.status-dropdown-portal .status-option:hover {
  background: var(--surface2, #f8fafc);
}
.status-dropdown-portal .status-option.active {
  background: var(--surface2, #f8fafc);
  color: var(--tx, #111827);
}
.status-dropdown-portal .status-chip-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
}
</style>
